import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Card,
  Text,
  InlineGrid,
  BlockStack,
  Divider,
  Thumbnail,
  InlineStack,
  Badge,
} from "@shopify/polaris";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import axios from "../../Assets/Lib/axios";
import TaskSlider from "../TaskSlider";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const move = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [moved] = sourceClone.splice(droppableSource.index, 1);
  destClone.splice(droppableDestination.index, 0, moved);
  return { source: sourceClone, destination: destClone };
};

export default function KanbanView({ rows = {}, statuses = [], onRefresh }) {
  // ✅ Build columns keyed by status.id but display status.name
  const initialColumns = useMemo(() => {
    const base = {};
    statuses.forEach((s) => {
      base[s.id] = {
        name: s.name,
        tasks: rows[s.name] || [],
      };
    });
    return base;
  }, [rows, statuses]);

  const [columns, setColumns] = useState(initialColumns);
  const [showSlider, setShowSlider] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const closeSlider = () => {
    setShowSlider(false);
    setSelectedTask(null);
  };
  useEffect(() => setColumns(initialColumns), [initialColumns]);
  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const fromCol = source.droppableId;
    const toCol = destination.droppableId;
    if (fromCol === toCol && source.index === destination.index) return;

    const task = columns[fromCol].tasks[source.index];

    // 🟢 Create shallow copies first
    const sourceTasks = Array.from(columns[fromCol].tasks);
    const destTasks = Array.from(columns[toCol].tasks);

    // 🧠 Optimistically move in UI immediately
    const [movedTask] = sourceTasks.splice(source.index, 1);
    destTasks.splice(destination.index, 0, movedTask);

    setColumns((prev) => ({
      ...prev,
      [fromCol]: { ...prev[fromCol], tasks: sourceTasks },
      [toCol]: { ...prev[toCol], tasks: destTasks },
    }));

    // 🕐 Now update backend quietly (no re-render blocking)
    try {
      await axios.patch(`/task-managers/${task.id}/status`, {
        status_id: Number(toCol),
      });

      console.log("✅ Updated status:", task.id, "→", toCol);

      // 🩵 Only refresh that single task count quietly after backend update
      setColumns((prev) => {
        const newPrev = { ...prev };
        newPrev[toCol].tasks = newPrev[toCol].tasks.map((t) =>
          t.id === task.id ? { ...t, status: { id: toCol } } : t
        );
        return newPrev;
      });
    } catch (err) {
      console.error("❌ Failed to update task status:", err);

      // 🔁 Revert UI optimistically if API failed
      setColumns((prev) => ({
        ...prev,
        [fromCol]: { ...prev[fromCol], tasks: columns[fromCol].tasks },
        [toCol]: { ...prev[toCol], tasks: columns[toCol].tasks },
      }));
    }
  };

  // const onDragEnd = (result) => {
  //   const { source, destination } = result;
  //   if (!destination) return;

  //   const fromCol = source.droppableId;
  //   const toCol = destination.droppableId;

  //   if (fromCol === toCol) {
  //     // ✅ reorder within same column
  //     const reordered = reorder(
  //       columns[fromCol].tasks,
  //       source.index,
  //       destination.index
  //     );
  //     setColumns((prev) => ({
  //       ...prev,
  //       [fromCol]: { ...prev[fromCol], tasks: reordered },
  //     }));
  //   } else {
  //     // ✅ move between columns
  //     const moved = move(
  //       columns[fromCol].tasks,
  //       columns[toCol].tasks,
  //       source,
  //       destination
  //     );

  //     setColumns((prev) => ({
  //       ...prev,
  //       [fromCol]: { ...prev[fromCol], tasks: moved.source },
  //       [toCol]: { ...prev[toCol], tasks: moved.destination },
  //     }));

  //     // ✅ send API request with status_id (numeric)
  //     const task = columns[fromCol].tasks[source.index];
  //     axios
  //       .patch(`/task-managers/${task.id}/status`, { status_id: Number(toCol) })
  //       .then(() => {
  //         console.log("✅ Task status updated:", task.id, "→", toCol);
  //         if (onRefresh) onRefresh();
  //       })
  //       .catch((err) => console.error("❌ Failed to update task status:", err));
  //   }
  // };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box padding="500">
        <InlineGrid
          columns={{ xs: 1, sm: 2, md: 3, lg: 6 }}
          gap="500"
          alignItems="start"
        >
          {Object.entries(columns).map(([statusId, data]) => (
            <KanbanColumn
              key={statusId}
              statusId={statusId}
              title={data.name}
              tasks={data.tasks}
              onTaskClick={(task) => {
                // 🟩 Close current slider if same task clicked again
                if (selectedTask?.id === task.id && showSlider) {
                  setShowSlider(false);
                  setSelectedTask(null);
                } else {
                  setSelectedTask(task);
                  setShowSlider(true);
                }
              }}
            />
          ))}
        </InlineGrid>
        {showSlider && (
          <TaskSlider task={selectedTask} closeSlider={closeSlider} />
        )}
      </Box>
    </DragDropContext>
  );
}

function KanbanColumn({ statusId, title, tasks, onTaskClick }) {
  console.log("tasks=", tasks);
  function daysFromToday(dateString) {
    if (!dateString) return null; // ✅ No deadline provided

    const target = new Date(dateString);
    if (isNaN(target.getTime())) return null; // ✅ Invalid date check

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);

    const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
    return diffDays;
  }
  return (
    <Box
      background="bg-surface-secondary"
      style={{
        backgroundColor: "#e8e9ea",
        padding: "0.5rem",
        borderRadius: "5px",
      }}
    >
      <BlockStack gap="100" padding="400">
        <InlineGrid columns="auto 1fr">
          <Text variant="headingSm" fontWeight="medium">
            {title}
          </Text>
          <Text tone="subdued">({tasks.length})</Text>
        </InlineGrid>
        <Divider />

        {/* ✅ droppableId must be the statusId as a string */}
        <Droppable droppableId={String(statusId)}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                minHeight: snapshot.isDraggingOver ? 200 : 80, // ⬅️ expand when dragging
                background: snapshot.isDraggingOver
                  ? "var(--p-color-bg-surface-hover)"
                  : "transparent",
                borderRadius: 8,
                // padding: snapshot.isDraggingOver ? "12px" : "8px", // ⬅️ make area feel larger
                // transition: "all 0.2s ease-in-out", // smooth grow/shrink
              }}
            >
              <BlockStack gap="200">
                {tasks.map((t, index) => (
                  <Draggable
                    key={String(t.id)}
                    draggableId={String(t.id)}
                    index={index}
                  >
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        style={{
                          ...dragProvided.draggableProps.style,
                          cursor: dragSnapshot.isDragging ? "grabbing" : "grab",
                          borderRadius: "12px",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                          padding: "16px",
                          // backgroundColor: "#fff",
                          backgroundColor:
                            String(t?.is_high) === "1" ? "#fde5e5" : "#fff",
                        }}
                        className="kanban-card"
                      >
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            onTaskClick(t); // ✅ open slider globally
                          }}
                          // onMouseEnter={(e) =>
                          //   (e.currentTarget.style.background =
                          //     "var(--p-color-bg-surface-hover)")
                          // }
                          // onMouseLeave={(e) =>
                          //   (e.currentTarget.style.background = "white")
                          // }
                          // style={{
                          //   transition:
                          //     "background 0.15s ease, box-shadow 0.15s ease",
                          //   userSelect: "none",
                          // }}
                        >
                          <BlockStack gap="200">
                            {/* 🟦 Title */}
                            <div
                              style={{
                                wordBreak: "break-word", // ✅ breaks long words
                                whiteSpace: "normal", // ✅ allow wrapping
                                overflowWrap: "anywhere", // ✅ breaks very long strings (no spaces)
                                lineHeight: "1.4",
                              }}
                            >
                              <Text variant="bodyMd" fontWeight="medium">
                                {t.title}
                              </Text>
                            </div>

                            {/* 🟩 Bottom row: thumbnails (left) + overdue + score + team (right) */}
                            <InlineGrid
                              columns="auto 1fr auto"
                              alignItems="center"
                              width="100%"
                            >
                              {/* LEFT: fixed area for up to 2 thumbnails */}
                              <InlineStack direction="row" gap={100}>
                                {t.screenshots?.slice(0, 2).map((img, i) => (
                                  <Thumbnail source={img} size="extraSmall" />
                                ))}
                              </InlineStack>

                              {/* MIDDLE: empty spacer column */}
                              <div />

                              {/* RIGHT: deadline badge + up to 2 avatars */}

                              <InlineStack direction="row" gap={100}>
                                <Badge
                                  tone={
                                    daysFromToday(t.deadline) < 0
                                      ? "critical"
                                      : daysFromToday(t.deadline) < 10
                                      ? "warning"
                                      : "default"
                                  }
                                >
                                  {daysFromToday(t.deadline) !== null
                                    ? `${daysFromToday(t.deadline)}d`
                                    : "NA"}
                                </Badge>

                                {t.assignees?.slice(0, 2).map((item, i) => (
                                  <img
                                    src={item.image_url}
                                    alt={item.name}
                                    width={20}
                                    height={20}
                                    style={{ borderRadius: "50%" }}
                                  />
                                ))}
                              </InlineStack>
                            </InlineGrid>
                          </BlockStack>
                        </div>

                        {/* <Card padding="300" shadow="100">
                        <Text variant="bodyMd" fontWeight="medium">
                          {t.title}
                        </Text>
                      </Card> */}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </BlockStack>
            </div>
          )}
        </Droppable>
      </BlockStack>
    </Box>
  );
}
